export class CollectionUtils {
  public static groupBy<T, K extends keyof T>(iter: Iterable<T>, key: K): Map<T[K], T[]>;
  public static groupBy<T, K extends keyof T, V>(iter: Iterable<T>, key: K, transformFn: (item: T) => V): Map<T[K], V[]>;
  public static groupBy<T, K extends keyof T, V>(iter: Iterable<T>, key: K, transformFn?: (item: T) => V): Map<T[K], (T | V)[]> {
    return CollectionUtils.groupByFn(iter, (item) => item[key], transformFn);
  }

  public static groupByFn<T, K>(iter: Iterable<T>, keyFn: (item: T) => K): Map<K, T[]>;
  public static groupByFn<T, K, V>(iter: Iterable<T>, keyFn: (item: T) => K, transformFn: (item: T) => V): Map<K, V[]>;
  public static groupByFn<T, K, V>(iter: Iterable<T>, keyFn: (item: T) => K, transformFn?: (item: T) => V): Map<K, (T | V)[]> {
    const m = new Map<K, (T | V)[]>();
    return CollectionUtils.groupByIntoExisting(m, iter, keyFn, transformFn);
  }

  public static groupByIntoExisting<T, K>(existingMap: Map<K, T[]>, iter: Iterable<T>, keyFn: (item: T) => K): Map<K, T[]>;
  public static groupByIntoExisting<T, K, V>(existingMap: Map<K, V[]>, iter: Iterable<T>, keyFn: (item: T) => K, transformFn: (item: T) => V): Map<K, V[]>;
  public static groupByIntoExisting<T, K, V>(existingMap: Map<K, (T | V)[]>, iter: Iterable<T>, keyFn: (item: T) => K, transformFn?: (item: T) => V): Map<K, (T | V)[]> {
    return IterableUtil.reduce(
      iter || [],
      (acc, item: T) => {
        const k = keyFn(item);
        const v = transformFn != null ? transformFn(item) : item;
        MapUtils.setDefaultFn(acc, k, () => []).push(v);
        return acc;
      },
      existingMap
    );
  }

  public static indexIntoExistingByFn<T, K>(existingMap: Map<K, T>, iter: Iterable<T>, keyFn: (item: T) => K): void;
  public static indexIntoExistingByFn<T, K, V>(existingMap: Map<K, V>, iter: Iterable<T>, keyFn: (item: T) => K, transformFn: (item: T) => V): void;
  public static indexIntoExistingByFn<T, K, V>(existingMap: Map<K, T | V>, iter: Iterable<T>, keyFn: (item: T) => K, transformFn?: (item: T) => V): void {
    for (const item of (iter || [])) {
      existingMap.set(keyFn(item), transformFn != null ? transformFn(item) : item);
    }
  }

  public static indexIntoExistingBy<T, K extends keyof T>(existingMap: Map<T[K], T>, iter: Iterable<T>, key: K): void;
  public static indexIntoExistingBy<T, K extends keyof T, V>(existingMap: Map<T[K], V>, iter: Iterable<T>, key: K, transformFn: (item: T) => V): void;
  public static indexIntoExistingBy<T, K extends keyof T, V>(existingMap: Map<T[K], T | V>, iter: Iterable<T>, key: K, transformFn?: (item: T) => V): void {
    CollectionUtils.indexIntoExistingByFn(existingMap, iter, item => item[key], transformFn);
  }

  public static indexByFn<T, K>(iter: Iterable<T>, keyFn: (item: T) => K): Map<K, T>;
  public static indexByFn<T, K, V>(iter: Iterable<T>, keyFn: (item: T) => K, transformFn: (item: T) => V): Map<K, V>;
  public static indexByFn<T, K, V>(iter: Iterable<T>, keyFn: (item: T) => K, transformFn?: (item: T) => V): Map<K, T | V> {
    const m = new Map<K, T | V>();
    CollectionUtils.indexIntoExistingByFn(m, iter, keyFn, transformFn);
    return m;
  }

  public static indexBy<T, K extends keyof T>(iter: Iterable<T>, key: K): Map<T[K], T>;
  public static indexBy<T, K extends keyof T, V>(iter: Iterable<T>, key: K, transformFn: (item: T) => V): Map<T[K], V>;
  public static indexBy<T, K extends keyof T, V>(iter: Iterable<T>, key: K, transformFn?: (item: T) => V): Map<T[K], T | V> {
    return CollectionUtils.indexByFn(iter, (item) => item[key], transformFn);
  }

  public static only<T>(arr: T[]): T | null {
    if (arr && arr.length) {
      if (arr.length === 1) {
        return arr[0];
      } else {
        throw new Error('Expected only a single element but found ' + arr.length);
      }
    } else {
      return null;
    }
  }

  public static toNullOrNotEmpty<T>(array: T[] | null): T[] | null {
    return array?.length > 0 ? array : null;
  }
}


type Tail<T extends readonly any[]> = T extends readonly [any, ...infer R] ? R : never;
// Get ids of recursive map as a Tuple
type NestedMapKeys<T> = T extends Map<infer K, infer V>
  ? V extends Map<any, any>
    ? [K, ...NestedMapKeys<V>]
    : [K]
  : never;
// Get value at the end of recursive Map
type NestedMapLeafValue<T> = T extends Map<any, infer V>
  ? V extends Map<any, any> ? NestedMapLeafValue<V> : V
  : never;
// Given a tuple, return a tuple where each element is an array of the original type or null
type As2dArray<T> = {
  [K in keyof T]: T[K][] | null;
};
// Turn key tuple into key array tuple for filter (each layer is an array of valid keys or null for "no filter")
export type NestedMapKeys2d<T> = As2dArray<NestedMapKeys<T>>;

export class MapUtils {

  public static setDefault<K, V>(map: Map<K, V>, key: K, defaultValue: V): V {
    return MapUtils.setDefaultFn(map, key, () => defaultValue);
  }

  public static setDefaultFn<K, V>(map: Map<K, V>, key: K, defaultValueFn: () => V): V {
    if (map.has(key)) {
      return map.get(key);
    }

    const defaultValue = defaultValueFn();
    map.set(key, defaultValue);

    return defaultValue;
  }

  public static pivot<K1, K2, V>(valueByKey1ByKey2: Map<K1, Map<K2, V>>): Map<K2, Map<K1, V>> {
    const result = new Map<K2, Map<K1, V>>();
    if (valueByKey1ByKey2) {
      for (const [key1, valueByKey2] of valueByKey1ByKey2.entries()) {
        if (valueByKey2) {
          for (const [key2, value] of valueByKey2.entries()) {
            MapUtils.setDefaultFn(result, key2, () => new Map<K1, V>()).set(key1, value);
          }
        }
      }
    }
    return result;
  }

  public static invert<K, V>(map: Map<K, V>, options?: {lastWriteWins?: boolean}): Map<V, K> {
    const result = new Map<V, K>();
    if (map != null) {
      for (const [key, value] of map.entries()) {
        if (!options?.lastWriteWins && result.has(value)) {
          throw new Error('Cannot invert map: duplicate value ' + String(value));
        }
        result.set(value, key);
      }
    }
    return result;
  }

  public static selectKeys<K, V>(map: Map<K, V>, keys: Iterable<K>): Map<K, V> {
    if (map != null) {
      const newMap = new Map<K, V>();
      IterableUtil.forEach(keys, key => {
        if (map.has(key)) {
          newMap.set(key, map.get(key));
        }
      });
      return newMap;
    } else {
      return map;
    }
  }

  public static mapValues<K, V, T>(map: Map<K, V>, mapFn: (value: V) => T): Map<K, T> {
    const mappedKeyValue = IterableUtil.map(map, ([key, value]) => [key, mapFn(value)] as const);
    return new Map(mappedKeyValue);
  }

  /** Filter layers of Nested Map by keys.
   * Should not filter the rest of layers if there are no filters for them.
   * @param filters tuple of keys of each nested Map layer.
   */
  public static filterNestedMap<K ,V>(nestedMap: Map<K, V>, filters: NestedMapKeys2d<Map<K, V>>): Map<K, V> {
    // KQ: Need casting because I haven't figured out how to make the rest of the tuple optional
    const trimmedFilter = ArrayUtils.trimEnd(filters) as NestedMapKeys2d<Map<K, V>>;
    return this.internalFilterNestedMap(nestedMap, trimmedFilter);
  }

  /** The actual nested map filter so that filter is only trimmed the first call */
  private static internalFilterNestedMap<K, V>(nestedMap: Map<K, V>, filters: NestedMapKeys2d<Map<K, V>>): Map<K, V> {
    if (nestedMap instanceof Map && filters?.length) {
      const currentMapIdFilter = filters[0];
      const filteredCurrentMap = currentMapIdFilter?.length
        ? MapUtils.selectKeys(nestedMap, currentMapIdFilter)
        : nestedMap;
      const innerMapIdFilter = filters.slice(1) as Tail<typeof filters>;
      if (innerMapIdFilter.length) {
        return MapUtils.mapValues(
          filteredCurrentMap,
          (innerMap) => innerMap instanceof Map
            ? this.internalFilterNestedMap(innerMap, innerMapIdFilter as any)
            : innerMap
        ) as Map<K, V>;
      } else {
        return filteredCurrentMap;
      }
    } else {
      return nestedMap;
    }
  }

  /** Iterate over all leaf (non-Map) entries of a nested Map structure. */
  public static forEachLeaf<T extends Map<any, any>>(nestedMap: T, callback: (value: NestedMapLeafValue<T>, keys: NestedMapKeys<T>) => void): void {
    this.internalForEachLeaf(nestedMap, [], callback as any);
  }

  /** Call `callback` on each leaf of a nested Map providing the value and keys(`keysPath`) traversed
   * @param nestedMap
   * @param keyPath the keys or path traversed to get to the current layer/leaf
   * @param onLeafFn the forEach callback on the leaf. Also provided the `keyPath` to the leaf
   */
  private static internalForEachLeaf(
    nestedMap: Map<any, any>,
    keyPath: any[],
    onLeafFn: (leafValue: any, keyPath: any[]) => void
  ): void {
    nestedMap.forEach((value, key) => {
      keyPath.push(key);
      if (value instanceof Map) {
        this.internalForEachLeaf(value, keyPath, onLeafFn);
      } else {
        onLeafFn(value, [...keyPath]);
      }
      keyPath.pop();
    });
  }

}

export class ArrayUtils {

  public static partitionAll<T>(array: T[], partitionSize: number, step: number = partitionSize): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += step) {
      result.push(array.slice(i, i + partitionSize));
    }
    return result;
  }

  public static partition<T>(array: T[], partitionSize: number, step: number = partitionSize): T[][] {
    const result: T[][] = [];
    for (let i = 0; i <= array.length - partitionSize; i += step) {
      result.push(array.slice(i, i + partitionSize));
    }
    return result;
  }

  public static flatten<T>(arrays: T[][]): T[] {
    return [].concat.apply([], arrays);
  }

  public static concat<T>(array1: T[] | null, array2: T[] | null): T[] | null {
    return (array1?.length || array2?.length)
        ? [...(array1 || []), ...(array2 || [])]
        : null;
  }

  public static arrayEquals<T>(array1: T[] | null, array2: T[] | null, comparator?: (item1: T, item2: T) => boolean): boolean {
    if (array1 == null && array2 == null) {
      return true;
    }
    if (array1?.length === array2?.length) {
      const itemComparator = comparator != null ? comparator : (item1, item2) => item1 === item2;
      return array1.every((item, index) => itemComparator(item, array2[index]));
    }
    return false;
  }

  public static containSameItems<T>(array1: T[] | null, array2: T[] | null): boolean {
    return array1?.length === array2?.length && SetUtils.isSubset(new Set(array1), new Set(array2));
  }

  public static asArray<T>(item: T | T[] | null): T[] {
    if (item == null) {
      return [];
    } else if (Array.isArray(item)) {
      return item;
    } else {
      return [item];
    }
  }

  public static findLastIndex<T>(array: T[], equalsFn: (t: T) => boolean): number {
    for (let i = array.length - 1; i >= 0; i--) {
      if (equalsFn(array[i])) {
        return i;
      }
    }
    return -1;
  }

  public static findLast<T>(array: T[], equalsFn: (t: T) => boolean): T | null {
    const lastIndex = ArrayUtils.findLastIndex(array, equalsFn);
    return lastIndex > -1 ? array[lastIndex] : null;
  }

  public static trimEnd<T>(arr: T[], shouldTrimFn: (item: T) => boolean = (item) => !item): T[] {
    return arr.reduceRight((acc, item) => {
      if (acc.length || !shouldTrimFn(item)) {
        acc.unshift(item);
      }
      return acc;
    }, <T[]>[]);
  }

  public static keep<T, T1>(list: Iterable<T> | null, mapFn: (item: T) => T1 | null | undefined): T1[] {
    return Array.from(IterableUtil.keep(list, mapFn));
  }
}

export class SetUtils {

  public static addAll<T>(set: Set<T>, items: Iterable<T> | null): Set<T> {
    if (items) {
      for (const item of items) {
        set.add(item);
      }
    }
    return set;
  }

  public static makeSet<T>(items?: T[]): Set<T> {
    return new Set<T>(items);
  }

  public static union<T>(left: Set<T> | null, right: Set<T> | null): Set<T> {
    const result = new Set<T>();
    left?.forEach(item => result.add(item));
    right?.forEach(item => result.add(item));
    return result;
  }

  public static difference<T>(left: Set<T> | null, right: Set<T> | null): Set<T> {
    const result = new Set<T>();
    left?.forEach(element => {
      if (!right?.has(element)) {
        result.add(element)
      }
    });

    return result;
  }

  public static intersection<T>(left: Set<T> | null, right: Set<T> | null): Set<T> {
    const result = new Set<T>();
    right?.forEach(element => {
      if (left?.has(element)) {
        result.add(element);
      }
    });

    return result;
  }

  public static isSubset<T>(superSet: Set<T> | null, subset: Set<T> | null): boolean {
    if (superSet == null || subset == null || subset?.size > superSet?.size) {
      return false;
    }

    for (let item of subset) {
      if (!superSet.has(item)) {
        return false;
      }
    }
    return true;
  }

  public static equal<T>(set1: Set<T> | null, set2: Set<T> | null): boolean {
    return set1?.size === set2?.size && ((set1 == null && set2 == null) || SetUtils.isSubset(set1, set2));
  }
}

export class IterableUtil {
  public static forEach<T>(iterable: Iterable<T>, callback: (item: T, index: number, iterable: Iterable<T>) => void) {
    if (iterable) {
      let index = 0;
      for (let item of iterable) {
        callback(item, index, iterable);
        index++;
      }
    }
  }

  public static reduce<T, R>(iterable: Iterable<T>,
                             mapFn: (accumulator: R, item: T, index: number, iterable: Iterable<T>) => R,
                             seed: R): R {
    let accumulator = seed;
    this.forEach(iterable, (item, index, iterable) => accumulator = mapFn(accumulator, item, index, iterable));
    return accumulator;
  }

  public static isIterable<T = any>(input: unknown): input is Iterable<T> {
    return input != null && typeof input !== 'string' && typeof input[Symbol.iterator] === 'function';
  }

  public static* map<T, T1>(iterable: Iterable<T> | null, mapFn: (item: T) => T1): Iterable<T1> {
    for (let item of iterable ?? []) {
      yield mapFn(item);
    }
  }

  public static* filter<T>(iterable: Iterable<T>, checkFn: (item: T) => boolean): Iterable<T> {
    for (let item of iterable ?? []) {
      if (checkFn(item)) {
        yield item;
      }
    }
  }

  public static first<T>(iterable: Iterable<T>): T {
    for (let item of iterable ?? []) {
      return item;
    }
    return null;
  }

  public static some<T>(iterable: Iterable<T>, checkFn: (item: T) => boolean): boolean {
    for (let _item of this.filter(iterable, checkFn)) {
      return true;
    }
    return false;
  }

  public static keep<T, T1>(iterable: Iterable<T> | null, mapFn: (item: T) => T1 | null | undefined): Iterable<T1> {
    const mapped = IterableUtil.map(iterable, mapFn);
    return IterableUtil.filter(<Iterable<T1>>mapped, (item) => item != null);
  }
}
